import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiList } from "@/lib/api";
import { formatClinicAddress } from "@/lib/clinic-information";
import { useActiveClinicaId } from "@/hooks/useActiveClinicaId";
import { InformacoesClinica, mergeClinicInformation } from "@/hooks/useInformacoesClinica";

type ClinicaSaas = {
  id: string;
  cliente_id?: string;
  nome?: string;
  razao_social?: string;
  nome_fantasia?: string;
  cnpj?: string;
  cro_clinica?: string;
  telefone?: string;
  celular?: string;
  email?: string;
  website?: string;
  endereco?: string;
  numero?: string;
  complemento?: string;
  bairro?: string;
  cidade?: string;
  estado?: string;
  cep?: string;
  logo_base64?: string;
};

export type RegistroComClinicaDocumento = {
  clinica_id?: string;
  clinica_dados?: Record<string, unknown> | Partial<InformacoesClinica> | null;
};

const hasAddress = (clinica?: Partial<InformacoesClinica> | ClinicaSaas | null) =>
  !!formatClinicAddress(clinica).trim();

function sameClinic(recordClinicaId: string, detail?: Record<string, unknown> | Partial<InformacoesClinica> | null) {
  if (!detail) return false;
  const detailClinicaId = typeof detail.clinica_id === "string" ? detail.clinica_id : "";
  return !!detailClinicaId && detailClinicaId === recordClinicaId;
}

export function useClinicaDocumento(informacoes?: InformacoesClinica | null) {
  const activeClinicaId = useActiveClinicaId();

  const { data: clinicas = [] } = useQuery({
    queryKey: ["clinicas-oficiais", activeClinicaId],
    queryFn: () => apiList<ClinicaSaas>("clinicas"),
    enabled: !!activeClinicaId,
    staleTime: 0,
    refetchOnMount: "always",
  });

  const activeClinica = useMemo(
    () => clinicas.find((clinica) => clinica.id === activeClinicaId) || null,
    [clinicas, activeClinicaId]
  );

  const clinicaAtual = useMemo(
    () => activeClinica ? mergeClinicInformation(informacoes || null, activeClinica) : null,
    [informacoes, activeClinica]
  );

  const clinicaDocumento = (registro?: RegistroComClinicaDocumento | null) => {
    const targetClinicaId = registro?.clinica_id || activeClinicaId;
    // A seleção da unidade é a fronteira do documento. Um registro de outra
    // unidade nunca pode fornecer cabeçalho enquanto ela não estiver ativa.
    if (!activeClinicaId || targetClinicaId !== activeClinicaId) return null;
    const clinica = clinicas.find((item) => item.id === targetClinicaId) || (targetClinicaId === activeClinicaId ? activeClinica : null);
    const snapshot = sameClinic(targetClinicaId, registro?.clinica_dados)
      ? (registro?.clinica_dados as Partial<InformacoesClinica>) || null
      : null;
    const detail = targetClinicaId === activeClinicaId && sameClinic(targetClinicaId, informacoes) ? informacoes || null : snapshot;
    const merged = clinica ? mergeClinicInformation(detail as InformacoesClinica | null, clinica) : null;

    if (merged && hasAddress(merged)) return merged;
    // Nunca usa dados de outra unidade como fallback. Se a unidade do registro
    // não estiver disponível, é mais seguro omitir o endereço do que trocá-lo.
    return merged;
  };

  return {
    activeClinicaId,
    clinicaAtual,
    clinicaDocumento,
  };
}
